import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Sparkles, X, ChevronLeft, Save, ZoomIn, Camera, Image as ImageIcon, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { LocationManagementModal } from '@/components/wine/LocationManagementModal';
import { BarcodeScanner } from '@/components/ui/BarcodeScanner';
import { searchProductByBarcode, type GeminiWineRecognition } from '@/lib/firebase';
import { findDuplicateWine } from '@/lib/duplicateUtils';
import { DuplicateModal } from '@/components/wine/DuplicateModal';
import type { WineFormData, WineType, Wine } from '@/types';

const wineTypeLabels: Record<WineType, string> = {
  red: 'Rosso',
  white: 'Bianco',
  rose: 'Rosato',
  sparkling: 'Spumante',
  dessert: 'Dessert',
  fortified: 'Fortificato',
  orange: 'Arancione',
  whisky: 'Whisky',
  rum: 'Rum',
  gin: 'Gin',
  grappa: 'Grappa',
  cognac: 'Cognac',
  liqueur: 'Liquore',
  other: 'Altro',
};

export const AddWinePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { wines, addWine, updateWine, identifyWineFromPhoto, identifyWineFromPhotoUrls, incrementWineQuantity, locations, isOwner } = useWines();

  // Determina se siamo in modalità modifica
  const isEditMode = Boolean(id);
  const existingWine = isEditMode ? wines.find((w) => w.id === id) : null;
  const isWineOwner = existingWine ? isOwner(existingWine) : true;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputBackRef = useRef<HTMLInputElement>(null);

  // Stati principali
  const [step, setStep] = useState<'choose-method' | 'upload-photo' | 'form'>('choose-method');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedImageBack, setSelectedImageBack] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [imagePreviewBack, setImagePreviewBack] = useState<string | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  // Immagini multiple per inserimento manuale (max 5)
  const [manualImages, setManualImages] = useState<{ file: File | null; preview: string }[]>([]);

  // Traccia se le immagini sono state modificate (per modalità edit)
  const [imagesChanged, setImagesChanged] = useState(false);

  // Stati AI e barcode
  const [aiRecognizing, setAiRecognizing] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [_barcodeSearching, setBarcodeSearching] = useState(false);
  const [barcodeImageUrl, setBarcodeImageUrl] = useState<string | null>(null);
  const [filledFields, setFilledFields] = useState<Set<string>>(new Set());
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Stati form
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stati per rilevamento duplicati
  const [duplicateWine, setDuplicateWine] = useState<Wine | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [incrementing, setIncrementing] = useState(false);

  const [formData, setFormData] = useState<WineFormData>({
    name: '',
    producer: '',
    type: 'red',
    vintage: undefined,
    grape: '',
    denomination: '',
    region: '',
    alcoholContent: undefined,
    estimatedValue: undefined,
    purchasePrice: undefined,
    quantity: undefined,
    pairings: [],
    tastingNotes: '',
    servingTemperature: '',
    glassType: '',
    storageTemperature: '',
    storageNotes: '',
    drinkByYear: undefined,
    agingPotential: '',
    locationId: undefined,
    location: '',
    personalNotes: '',
    rating: undefined,
    consumedNotes: '',
  });

  // Carica dati del vino esistente in modalità modifica
  useEffect(() => {
    if (isEditMode && existingWine) {
      // Pre-compila il form con i dati esistenti
      setFormData({
        name: existingWine.name,
        producer: existingWine.producer,
        type: existingWine.type,
        vintage: existingWine.vintage,
        grape: existingWine.grape || '',
        denomination: existingWine.denomination || '',
        region: existingWine.region || '',
        alcoholContent: existingWine.alcoholContent,
        estimatedValue: existingWine.estimatedValue,
        purchasePrice: existingWine.purchasePrice,
        quantity: existingWine.quantity || 1,
        pairings: existingWine.pairings || [],
        tastingNotes: existingWine.tastingNotes || '',
        servingTemperature: existingWine.servingTemperature || '',
        glassType: existingWine.glassType || '',
        storageTemperature: existingWine.storageTemperature || '',
        storageNotes: existingWine.storageNotes || '',
        drinkByYear: existingWine.drinkByYear,
        agingPotential: existingWine.agingPotential || '',
        location: existingWine.location || '',
        locationId: existingWine.locationId,
        personalNotes: existingWine.personalNotes || '',
        rating: existingWine.rating,
        consumedNotes: existingWine.consumedNotes || '',
      });

      // Pre-carica le anteprime delle immagini se presenti
      const imageUrls = existingWine.imageUrls || (existingWine.imageUrl ? [existingWine.imageUrl] : []);
      if (imageUrls.length > 0) {
        // Prima immagine come immagine principale
        setImagePreview(imageUrls[0]);

        // Seconda immagine come immagine retro (se presente)
        if (imageUrls.length > 1) {
          setImagePreviewBack(imageUrls[1]);
        }

        // Immagini aggiuntive come manualImages (dalla terza in poi)
        if (imageUrls.length > 2) {
          const additionalImages = imageUrls.slice(2).map(url => ({
            file: null as any, // Non abbiamo il File originale, solo l'URL
            preview: url
          }));
          setManualImages(additionalImages);
        }
      }

      // Vai direttamente al form in modalità modifica
      setStep('form');
    }
  }, [isEditMode, existingWine]);

  // Handle image selection (front)
  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setImagesChanged(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle image selection (back)
  const handleImageBackSelect = (file: File) => {
    setSelectedImageBack(file);
    setImagesChanged(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviewBack(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Handle manual images (max 5 totali includendo selected + selectedBack)
  const handleManualImageAdd = (file: File) => {
    const totalPhotos = (selectedImage ? 1 : 0) + (selectedImageBack ? 1 : 0) + manualImages.length;

    if (totalPhotos >= 5) {
      setError('Puoi caricare massimo 5 foto in totale');
      return;
    }

    setImagesChanged(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setManualImages(prev => [...prev, { file, preview: reader.result as string }]);
    };
    reader.readAsDataURL(file);
  };

  const handleManualImageRemove = (index: number) => {
    setImagesChanged(true);
    setManualImages(prev => prev.filter((_, i) => i !== index));
  };

  // Rimuovi foto fronte
  const handleRemoveFrontImage = () => {
    setImagesChanged(true);
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Rimuovi foto retro
  const handleRemoveBackImage = () => {
    setImagesChanged(true);
    setSelectedImageBack(null);
    setImagePreviewBack(null);
  };

  // Recognize wine with AI (compila solo campi vuoti)
  const handleRecognizeWithAI = async () => {
    const hasImages = selectedImage || selectedImageBack || imagePreview || imagePreviewBack || manualImages.length > 0;
    if (!hasImages) return;

    setAiRecognizing(true);
    setError(null);

    try {
      // Se in modalità modifica, prepara i dati esistenti da passare all'AI
      let existingWineData: Partial<GeminiWineRecognition> | undefined;
      if (isEditMode && existingWine) {
        const supportedTypes = ['red', 'white', 'rose', 'sparkling', 'dessert', 'fortified'];
        const mappedType = supportedTypes.includes(existingWine.type)
          ? existingWine.type as 'red' | 'white' | 'rose' | 'sparkling' | 'dessert' | 'fortified'
          : undefined;

        existingWineData = {
          name: existingWine.name,
          producer: existingWine.producer,
          vintage: existingWine.vintage,
          type: mappedType,
          grape: existingWine.grape,
          denomination: existingWine.denomination,
          region: existingWine.region,
          alcoholContent: existingWine.alcoholContent,
          estimatedValue: existingWine.estimatedValue,
          pairings: existingWine.pairings,
          tastingNotes: existingWine.tastingNotes,
          servingTemperature: existingWine.servingTemperature,
          glassType: existingWine.glassType,
          storageTemperature: existingWine.storageTemperature,
          storageNotes: existingWine.storageNotes,
          drinkByYear: existingWine.drinkByYear,
          agingPotential: existingWine.agingPotential,
        };
      }

      let results;

      if (selectedImage) {
        // Nuove immagini caricate dall'utente
        const imagesToAnalyze = selectedImageBack
          ? [selectedImage, selectedImageBack]
          : selectedImage;
        results = await identifyWineFromPhoto(imagesToAnalyze, existingWineData);
      } else if (imagePreview && !selectedImage) {
        // Modalità modifica: immagini già su Storage — le passa come URL alla Cloud Function
        // che le scarica server-side evitando il CORS del browser
        const urls = [imagePreview, ...(imagePreviewBack ? [imagePreviewBack] : [])];
        results = await identifyWineFromPhotoUrls(urls, existingWineData);
      } else {
        // Immagini manuali (File objects)
        const validImages = manualImages.filter(img => img.file !== null).map(img => img.file!);
        const imagesToAnalyze = validImages.length === 1 ? validImages[0] : validImages;
        results = await identifyWineFromPhoto(imagesToAnalyze, existingWineData);
      }

      if (results.length > 0) {
        const result = results[0];
        const newFilledFields = new Set(filledFields);

        setFormData(prev => {
          const updated = { ...prev };

          if (!filledFields.has('name') && result.name) {
            updated.name = result.name;
            newFilledFields.add('name');
          }
          if (!filledFields.has('producer') && result.producer) {
            updated.producer = result.producer;
            newFilledFields.add('producer');
          }
          if (!filledFields.has('type') && result.type) {
            updated.type = result.type;
            newFilledFields.add('type');
          }
          if (!filledFields.has('vintage') && result.vintage) {
            updated.vintage = result.vintage;
            newFilledFields.add('vintage');
          }
          if (!filledFields.has('grape') && result.grape) {
            updated.grape = result.grape;
            newFilledFields.add('grape');
          }
          if (!filledFields.has('denomination') && result.denomination) {
            updated.denomination = result.denomination;
            newFilledFields.add('denomination');
          }
          if (!filledFields.has('region') && result.region) {
            updated.region = result.region;
            newFilledFields.add('region');
          }
          if (!filledFields.has('alcoholContent') && result.alcoholContent) {
            updated.alcoholContent = result.alcoholContent;
            newFilledFields.add('alcoholContent');
          }
          if (!filledFields.has('estimatedValue') && result.estimatedValue) {
            updated.estimatedValue = result.estimatedValue;
            newFilledFields.add('estimatedValue');
          }
          if (!filledFields.has('pairings') && result.pairings) {
            updated.pairings = result.pairings;
            newFilledFields.add('pairings');
          }
          if (!filledFields.has('tastingNotes') && result.tastingNotes) {
            updated.tastingNotes = result.tastingNotes;
            newFilledFields.add('tastingNotes');
          }
          if (!filledFields.has('servingTemperature') && result.servingTemperature) {
            updated.servingTemperature = result.servingTemperature;
            newFilledFields.add('servingTemperature');
          }
          if (!filledFields.has('glassType') && result.glassType) {
            updated.glassType = result.glassType;
            newFilledFields.add('glassType');
          }
          if (!filledFields.has('storageTemperature') && result.storageTemperature) {
            updated.storageTemperature = result.storageTemperature;
            newFilledFields.add('storageTemperature');
          }
          if (!filledFields.has('storageNotes') && result.storageNotes) {
            updated.storageNotes = result.storageNotes;
            newFilledFields.add('storageNotes');
          }
          if (!filledFields.has('drinkByYear') && result.drinkByYear) {
            updated.drinkByYear = result.drinkByYear;
            newFilledFields.add('drinkByYear');
          }
          if (!filledFields.has('agingPotential') && result.agingPotential) {
            updated.agingPotential = result.agingPotential;
            newFilledFields.add('agingPotential');
          }

          return updated;
        });

        setFilledFields(newFilledFields);
      }
    } catch (err) {
      console.error('AI recognition error:', err);
      // Mostra il messaggio di errore specifico se disponibile
      const errorMessage = err instanceof Error
        ? err.message
        : 'Errore nel riconoscimento AI. Riprova o inserisci manualmente.';
      setError(errorMessage);
    } finally {
      setAiRecognizing(false);
      // Mostra bottoni rapidi se in modalità modifica
      if (isEditMode) {
        setShowQuickActions(true);
      }
    }
  };

  // Handle barcode scan
  const handleBarcodeScan = async (barcode: string) => {
    setShowBarcodeScanner(false);
    setBarcodeSearching(true);
    setError(null);

    try {
      const product = await searchProductByBarcode(barcode);

      if (!product.found) {
        setError('Prodotto non trovato nel database. Puoi compilare manualmente i dati.');
        setStep('form');
        return;
      }

      // Compila campi trovati
      const filled = new Set<string>();
      if (product.name) {
        filled.add('name');
        setFormData(prev => ({ ...prev, name: product.name! }));
      }
      if (product.producer) {
        filled.add('producer');
        setFormData(prev => ({ ...prev, producer: product.producer! }));
      }
      if (product.region) {
        filled.add('region');
        setFormData(prev => ({ ...prev, region: product.region! }));
      }
      if (product.alcoholContent) {
        filled.add('alcoholContent');
        setFormData(prev => ({ ...prev, alcoholContent: product.alcoholContent! }));
      }

      setFilledFields(filled);

      // Salva immagine se disponibile
      if (product.imageUrl) {
        setBarcodeImageUrl(product.imageUrl);
      }

      setStep('form');
    } catch (err) {
      console.error('Barcode search error:', err);
      setError('Errore nella ricerca del prodotto. Riprova o inserisci manualmente.');
      setStep('form');
    } finally {
      setBarcodeSearching(false);
    }
  };

  // Save wine
  const handleSaveWine = async () => {
    if (!formData.name || !formData.producer) {
      setError('Nome e produttore sono obbligatori');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && id) {
        // Modalità modifica
        if (imagesChanged) {
          // Raccogli immagini: mix di URL esistenti (da mantenere) e nuovi File
          const imagesToUpdate: (File | string)[] = [];

          // Prima immagine: usa File se disponibile, altrimenti mantieni URL esistente
          if (selectedImage) {
            imagesToUpdate.push(selectedImage);
          } else if (imagePreview && !selectedImage) {
            // imagePreview è un URL esistente, mantienilo
            imagesToUpdate.push(imagePreview);
          }

          // Seconda immagine: usa File se disponibile, altrimenti mantieni URL esistente
          if (selectedImageBack) {
            imagesToUpdate.push(selectedImageBack);
          } else if (imagePreviewBack && !selectedImageBack) {
            // imagePreviewBack è un URL esistente, mantienilo
            imagesToUpdate.push(imagePreviewBack);
          }

          // Immagini aggiuntive: usa File se disponibile, altrimenti mantieni URL esistente
          manualImages.forEach(img => {
            if (img.file) {
              imagesToUpdate.push(img.file);
            } else if (img.preview) {
              // img.preview è un URL esistente, mantienilo
              imagesToUpdate.push(img.preview);
            }
          });

          await updateWine(id, formData, imagesToUpdate);
        } else {
          // Nessuna modifica alle immagini
          await updateWine(id, formData, undefined);
        }

        // Piccolo delay per dare tempo al listener di processare l'aggiornamento
        await new Promise(resolve => setTimeout(resolve, 200));
        navigate(`/wine/${id}`);
      } else {
        // Modalità aggiunta - controlla prima se esiste un duplicato
        if (!user) {
          setError('Utente non autenticato');
          return;
        }

        const duplicate = findDuplicateWine(formData, wines, user.id);
        if (duplicate) {
          // Duplicato trovato - mostra modal e ferma il salvataggio
          setDuplicateWine(duplicate);
          setShowDuplicateModal(true);
          setSaving(false);
          return;
        }

        // Nessun duplicato - procedi con l'aggiunta normale
        // Modalità aggiunta - raccogli solo i File
        const allImages: File[] = [];
        if (selectedImage) allImages.push(selectedImage);
        if (selectedImageBack) allImages.push(selectedImageBack);
        manualImages.forEach(img => {
          if (img.file) allImages.push(img.file);
        });
        // Modalità aggiunta - va alla scheda del vino appena creato
        const newWineId = await addWine(formData, allImages.length > 0 ? allImages : undefined);
        navigate(`/wine/${newWineId}`);
      }
    } catch (err) {
      console.error('Error saving wine:', err);
      setError('Errore nel salvataggio del vino. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  // Handler per incrementare quantità del vino duplicato
  const handleIncrementDuplicate = async () => {
    if (!duplicateWine) return;

    setIncrementing(true);
    try {
      await incrementWineQuantity(duplicateWine.id);
      setShowDuplicateModal(false);
      setDuplicateWine(null);
      navigate('/');
    } catch (err) {
      console.error('Error incrementing wine quantity:', err);
      setError('Errore durante l\'incremento della quantità');
    } finally {
      setIncrementing(false);
    }
  };

  // Handler per inserire comunque come nuovo vino
  const handleInsertAnyway = async () => {
    setShowDuplicateModal(false);
    setDuplicateWine(null);

    if (!user) {
      setError('Utente non autenticato');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Raccogli le immagini
      const allImages: File[] = [];
      if (selectedImage) allImages.push(selectedImage);
      if (selectedImageBack) allImages.push(selectedImageBack);
      manualImages.forEach(img => {
        if (img.file) allImages.push(img.file);
      });

      // Aggiungi il vino come nuovo
      const newWineId = await addWine(formData, allImages.length > 0 ? allImages : undefined);
      navigate(`/wine/${newWineId}`);
    } catch (err) {
      console.error('Error saving wine:', err);
      setError('Errore nel salvataggio del vino. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  // Handler per ignorare e tornare alla home
  const handleIgnore = () => {
    setShowDuplicateModal(false);
    setDuplicateWine(null);
    navigate('/');
  };

  // Update form field
  const updateFormField = (field: keyof WineFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Barcode Scanner */}
        {showBarcodeScanner && (
          <BarcodeScanner
            onScan={handleBarcodeScan}
            onClose={() => setShowBarcodeScanner(false)}
          />
        )}

        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Button variant="ghost" icon={<ChevronLeft className="w-5 h-5" />} onClick={() => navigate(isEditMode ? `/wine/${id}` : '/')}>
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isEditMode ? 'Modifica Vino' : 'Aggiungi Vino'}
          </h1>
        </div>

        {/* Fullscreen Image Modal */}
        <AnimatePresence>
          {fullscreenImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
              onClick={() => setFullscreenImage(null)}
            >
              <button
                onClick={() => setFullscreenImage(null)}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={fullscreenImage}
                alt="Fullscreen preview"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 1: Choose Method */}
        {step === 'choose-method' && (
          <Card>
            <CardHeader>
              <CardTitle>Come vuoi aggiungere il vino?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Carica Foto */}
                <button
                  onClick={() => setStep('upload-photo')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-wine-purple hover:bg-wine-purple/5 transition-all group"
                >
                  <Camera className="w-12 h-12 text-wine-purple mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-gray-900 mb-1">Carica Foto</h3>
                  <p className="text-sm text-gray-600">Riconosci con AI dalla foto dell'etichetta</p>
                </button>

                {/* Scansiona Barcode - TEMPORANEAMENTE NASCOSTO */}
                {/* <button
                  onClick={() => setShowBarcodeScanner(true)}
                  disabled={barcodeSearching}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-wine-purple hover:bg-wine-purple/5 transition-all group disabled:opacity-50"
                >
                  <Barcode className="w-12 h-12 text-wine-purple mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-gray-900 mb-1">Scansiona Barcode</h3>
                  <p className="text-sm text-gray-600">Cerca nel database mondiale</p>
                </button> */}

                {/* Manuale */}
                <button
                  onClick={() => setStep('form')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-wine-purple hover:bg-wine-purple/5 transition-all group"
                >
                  <Upload className="w-12 h-12 text-wine-purple mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-gray-900 mb-1">Inserisci Manualmente</h3>
                  <p className="text-sm text-gray-600">Compila tutti i campi a mano</p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Upload Photo */}
        {step === 'upload-photo' && (
          <Card>
            <CardHeader>
              <CardTitle>Carica Foto Bottiglia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Foto Fronte */}
                {imagePreview ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Foto Fronte *</label>
                    <div className="relative bg-gray-100 rounded-lg">
                      <img
                        src={imagePreview}
                        alt="Wine front preview"
                        className="w-full h-96 object-contain rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                        onClick={() => setFullscreenImage(imagePreview)}
                      />
                      <button
                        onClick={() => {
                          setSelectedImage(null);
                          setImagePreview(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5">
                        <ZoomIn className="w-3.5 h-3.5" />
                        Clicca per ingrandire
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Foto Fronte *</label>
                    {/* Mobile: Galleria e Camera separati */}
                    <div className="lg:hidden flex gap-3">
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e: any) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageSelect(file);
                          };
                          input.click();
                        }}
                        className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                      >
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700">Galleria</p>
                      </button>
                      <button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.setAttribute('capture', 'environment');
                          input.onchange = (e: any) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageSelect(file);
                          };
                          input.click();
                        }}
                        className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                      >
                        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700">Scatta</p>
                      </button>
                    </div>
                    {/* Desktop: Upload standard */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="hidden lg:block border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                    >
                      <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-900 mb-2">Carica foto bottiglia</p>
                      <p className="text-sm text-gray-600">Clicca o trascina</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageSelect(file);
                      }}
                      className="hidden"
                    />
                  </div>
                )}

                {/* Foto Retro (opzionale) */}
                {selectedImage && (
                  <>
                    {imagePreviewBack ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Foto Retro (opzionale)</label>
                        <div className="relative bg-gray-100 rounded-lg">
                          <img
                            src={imagePreviewBack}
                            alt="Wine back preview"
                            className="w-full h-96 object-contain rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                            onClick={() => setFullscreenImage(imagePreviewBack)}
                          />
                          <button
                            onClick={() => {
                              setSelectedImageBack(null);
                              setImagePreviewBack(null);
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5">
                            <ZoomIn className="w-3.5 h-3.5" />
                            Clicca per ingrandire
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Foto Retro (opzionale - consigliata per annata e gradazione)
                        </label>
                        {/* Mobile */}
                        <div className="lg:hidden flex gap-3">
                          <button
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e: any) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageBackSelect(file);
                              };
                              input.click();
                            }}
                            className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                          >
                            <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">Galleria</p>
                          </button>
                          <button
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.setAttribute('capture', 'environment');
                              input.onchange = (e: any) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageBackSelect(file);
                              };
                              input.click();
                            }}
                            className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                          >
                            <Camera className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">Scatta</p>
                          </button>
                        </div>
                        {/* Desktop */}
                        <div
                          onClick={() => fileInputBackRef.current?.click()}
                          className="hidden lg:block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                        >
                          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-900 mb-1">Carica foto retro</p>
                          <p className="text-xs text-gray-600">Migliora riconoscimento annata e gradazione</p>
                        </div>
                        <input
                          ref={fileInputBackRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageBackSelect(file);
                          }}
                          className="hidden"
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Actions */}
                {selectedImage && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setStep('choose-method')} fullWidth>
                      Indietro
                    </Button>
                    <Button
                      icon={<Sparkles className="w-5 h-5" />}
                      onClick={async () => {
                        await handleRecognizeWithAI();
                        setStep('form');
                      }}
                      loading={aiRecognizing}
                      fullWidth
                    >
                      🪄 Riconosci con AI
                    </Button>
                  </div>
                )}

                {/* Error Alert - sotto bottone AI */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800"
                    >
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Form */}
        {step === 'form' && (
          <Card>
            <CardHeader>
              <CardTitle>Dettagli Vino</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Immagini - Prima grande, altre piccole */}
                {(() => {
                  // Raccogli tutte le foto disponibili
                  const allPhotos: {
                    src: string;
                    label: string;
                    canRemove: boolean;
                    removeHandler?: () => void;
                    removeIndex?: number;
                  }[] = [];

                  if (imagePreview) allPhotos.push({
                    src: imagePreview,
                    label: 'Foto Fronte',
                    canRemove: true,
                    removeHandler: handleRemoveFrontImage
                  });
                  if (imagePreviewBack) allPhotos.push({
                    src: imagePreviewBack,
                    label: 'Foto Retro',
                    canRemove: true,
                    removeHandler: handleRemoveBackImage
                  });
                  if (barcodeImageUrl && !imagePreview) allPhotos.push({
                    src: barcodeImageUrl,
                    label: 'Foto dal Database',
                    canRemove: false
                  });

                  manualImages.forEach((img, idx) => {
                    allPhotos.push({
                      src: img.preview,
                      label: `Foto ${allPhotos.length + 1}`,
                      canRemove: true,
                      removeIndex: idx
                    });
                  });

                  if (allPhotos.length === 0) return null;

                  const [firstPhoto, ...otherPhotos] = allPhotos;

                  return (
                    <div className="space-y-3">
                      {/* Prima foto - Grande */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {firstPhoto.label}
                        </label>
                        <div className="relative bg-gray-100 rounded-lg group">
                          <img
                            src={firstPhoto.src}
                            alt={firstPhoto.label}
                            className="w-full h-64 object-contain rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                            onClick={() => setFullscreenImage(firstPhoto.src)}
                          />
                          {firstPhoto.canRemove && (
                            <button
                              onClick={() => {
                                if (firstPhoto.removeHandler) {
                                  firstPhoto.removeHandler();
                                } else if (firstPhoto.removeIndex !== undefined) {
                                  handleManualImageRemove(firstPhoto.removeIndex);
                                }
                              }}
                              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          )}
                          <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5">
                            <ZoomIn className="w-3.5 h-3.5" />
                            Clicca per ingrandire
                          </div>
                        </div>
                      </div>

                      {/* Altre foto - Piccole */}
                      {otherPhotos.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Altre foto ({otherPhotos.length})
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {otherPhotos.map((photo, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={photo.src}
                                  alt={photo.label}
                                  className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setFullscreenImage(photo.src)}
                                />
                                {photo.canRemove && (
                                  <button
                                    onClick={() => {
                                      if (photo.removeHandler) {
                                        photo.removeHandler();
                                      } else if (photo.removeIndex !== undefined) {
                                        handleManualImageRemove(photo.removeIndex);
                                      }
                                    }}
                                    className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Upload foto aggiuntive (sempre disponibile, max 5 totali) */}
                {!barcodeImageUrl && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedImage || selectedImageBack ? 'Aggiungi Altre Foto (max 5 totali)' : 'Foto Bottiglia (fino a 5)'}
                      </label>

                      {/* Calcola foto totali */}
                      {(() => {
                        const totalPhotos = (selectedImage ? 1 : 0) + (selectedImageBack ? 1 : 0) + manualImages.length;
                        const canAddMore = totalPhotos < 5;

                        return (
                          <>
                            {/* Bottoni aggiungi foto */}
                            {canAddMore && (
                              <>
                                {/* Mobile */}
                                <div className="lg:hidden flex gap-3">
                                  <button
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = 'image/*';
                                      input.onchange = (e: any) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleManualImageAdd(file);
                                      };
                                      input.click();
                                    }}
                                    className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                                  >
                                    <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm font-medium text-gray-700">Galleria</p>
                                  </button>
                                  <button
                                    onClick={() => {
                                      const input = document.createElement('input');
                                      input.type = 'file';
                                      input.accept = 'image/*';
                                      input.setAttribute('capture', 'environment');
                                      input.onchange = (e: any) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleManualImageAdd(file);
                                      };
                                      input.click();
                                    }}
                                    className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                                  >
                                    <Camera className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm font-medium text-gray-700">Scatta</p>
                                  </button>
                                </div>

                                {/* Desktop */}
                                <button
                                  onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'image/*';
                                    input.onchange = (e: any) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleManualImageAdd(file);
                                    };
                                    input.click();
                                  }}
                                  className="hidden lg:block w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-wine-purple hover:bg-wine-purple/5 transition-all"
                                >
                                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                  <p className="text-sm font-medium text-gray-900 mb-1">
                                    Aggiungi foto ({totalPhotos}/5)
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {selectedImage ? 'Aggiungi altre foto dell\'etichetta' : 'Clicca per caricare foto dell\'etichetta'}
                                  </p>
                                </button>
                              </>
                            )}

                            {!canAddMore && (
                              <p className="text-sm text-gray-500 text-center">
                                Hai raggiunto il massimo di 5 foto
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                  </div>
                )}

                {/* Bottone AI unificato - mostra se ci sono foto (nuove o esistenti) */}
                {(selectedImage || selectedImageBack || imagePreview || imagePreviewBack || manualImages.length > 0) && !barcodeImageUrl && (
                  <>
                    <Button
                      fullWidth
                      icon={<Sparkles className="w-5 h-5" />}
                      onClick={handleRecognizeWithAI}
                      loading={aiRecognizing}
                      variant="outline"
                    >
                      🪄 {isEditMode ? 'Rilancia Analisi AI' : 'Usa AI per completare campi mancanti'}
                    </Button>

                    {/* Error Alert - sotto bottone AI */}
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"
                        >
                          {error}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Bottoni rapidi Salva/Indietro - mostrati dopo analisi AI in edit mode */}
                    <AnimatePresence>
                      {isEditMode && showQuickActions && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="flex gap-3 mt-3 pt-3 border-t border-gray-200"
                        >
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/wine/${id}`)}
                            fullWidth
                          >
                            Indietro
                          </Button>
                          <Button
                            icon={<Save className="w-5 h-5" />}
                            onClick={handleSaveWine}
                            loading={saving}
                            fullWidth
                          >
                            Salva Vino
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}

                {/* Upload Photo se viene da barcode */}
                {barcodeImageUrl && !selectedImage && (
                  <Button
                    fullWidth
                    icon={<Camera className="w-5 h-5" />}
                    onClick={() => setStep('upload-photo')}
                    variant="outline"
                  >
                    📷 Aggiungi Foto Personale
                  </Button>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Nome Vino *"
                    value={formData.name}
                    onChange={(e) => updateFormField('name', e.target.value)}
                    placeholder="es. Barolo Riserva"
                    required
                  />
                  <Input
                    label="Produttore *"
                    value={formData.producer}
                    onChange={(e) => updateFormField('producer', e.target.value)}
                    placeholder="es. Marchesi di Barolo"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => updateFormField('type', e.target.value as WineType)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wine-purple focus:border-transparent"
                    >
                      {Object.entries(wineTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Annata"
                    type="number"
                    value={formData.vintage || ''}
                    onChange={(e) => updateFormField('vintage', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="2018"
                  />
                  <Input
                    label="Gradazione (%)"
                    type="number"
                    step="0.1"
                    value={formData.alcoholContent || ''}
                    onChange={(e) => updateFormField('alcoholContent', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="es. 13.5"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Vitigno"
                    value={formData.grape || ''}
                    onChange={(e) => updateFormField('grape', e.target.value)}
                    placeholder="es. Nebbiolo"
                  />
                  <Input
                    label="Denominazione"
                    value={formData.denomination || ''}
                    onChange={(e) => updateFormField('denomination', e.target.value)}
                    placeholder="es. DOCG"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Regione"
                    value={formData.region || ''}
                    onChange={(e) => updateFormField('region', e.target.value)}
                    placeholder="es. Piemonte"
                  />
                  <Input
                    label="Temperatura Servizio"
                    value={formData.servingTemperature || ''}
                    onChange={(e) => updateFormField('servingTemperature', e.target.value)}
                    placeholder="es. 16-18°C"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Valore Stimato (€)"
                    type="number"
                    step="0.01"
                    value={formData.estimatedValue || ''}
                    onChange={(e) => updateFormField('estimatedValue', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="45.00"
                  />
                  <Input
                    label="Prezzo d'Acquisto (€)"
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice || ''}
                    onChange={(e) => updateFormField('purchasePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="38.00"
                  />
                  <Input
                    label="Quantità Bottiglie"
                    type="number"
                    min="1"
                    value={formData.quantity !== undefined ? formData.quantity : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === '0') {
                        updateFormField('quantity', undefined);
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 1) {
                          updateFormField('quantity', num);
                        }
                      }
                    }}
                    placeholder="1"
                  />
                </div>

                {/* Location fields - locationId (dropdown) + location (notes) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Location Dropdown with Manage Button */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Posizione {!isWineOwner && <span className="text-xs text-gray-500">(solo proprietario)</span>}
                    </label>
                    {!isWineOwner && formData.locationId && !locations.find(l => l.id === formData.locationId) ? (
                      // Mostra messaggio quando è una posizione del proprietario
                      <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                        Posizione del proprietario
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Select
                            options={[
                              { value: '', label: 'Nessuna posizione' },
                              ...locations.map(loc => ({
                                value: loc.id,
                                label: loc.name
                              }))
                            ]}
                            value={formData.locationId || ''}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const value = e.target.value;
                              updateFormField('locationId', value === '' ? undefined : value);
                            }}
                            disabled={!isWineOwner}
                          />
                        </div>
                        {isWineOwner && (
                          <Button
                            type="button"
                            variant="outline"
                            icon={<MapPin className="w-5 h-5" />}
                            onClick={() => setShowLocationModal(true)}
                            title="Gestisci posizioni"
                            className="flex-shrink-0"
                          >
                            <span className="sr-only">Gestisci posizioni</span>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Location Notes (ex location field) */}
                  <Input
                    label="Note Posizione"
                    value={formData.location || ''}
                    onChange={(e) => updateFormField('location', e.target.value)}
                    placeholder="es. Scaffale A, Ripiano 3"
                    disabled={!isWineOwner}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note di Degustazione</label>
                  <textarea
                    value={formData.tastingNotes || ''}
                    onChange={(e) => updateFormField('tastingNotes', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wine-purple focus:border-transparent"
                    placeholder="Aromi, sapori, caratteristiche..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo di Bicchiere Consigliato
                    {formData.glassType && (
                      <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                    )}
                  </label>
                  <Input
                    value={formData.glassType || ''}
                    onChange={(e) => updateFormField('glassType', e.target.value)}
                    placeholder="es. Ballon Bordeaux, Flute, Calice Borgogna"
                  />
                </div>

                <div className="col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span>📦</span> Conservazione e Invecchiamento
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Temperatura di Conservazione
                        {formData.storageTemperature && (
                          <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                        )}
                      </label>
                      <Input
                        value={formData.storageTemperature || ''}
                        onChange={(e) => updateFormField('storageTemperature', e.target.value)}
                        placeholder="es. 12-14°C"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Da Bere Entro (Anno)
                        {formData.drinkByYear && (
                          <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                        )}
                      </label>
                      <Input
                        type="number"
                        value={formData.drinkByYear || ''}
                        onChange={(e) => updateFormField('drinkByYear', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="es. 2030"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Potenziale di Invecchiamento
                        {formData.agingPotential && (
                          <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                        )}
                      </label>
                      <Input
                        value={formData.agingPotential || ''}
                        onChange={(e) => updateFormField('agingPotential', e.target.value)}
                        placeholder="es. 5-10 anni, Pronto da bere"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Note sulla Conservazione
                        {formData.storageNotes && (
                          <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                        )}
                      </label>
                      <Input
                        value={formData.storageNotes || ''}
                        onChange={(e) => updateFormField('storageNotes', e.target.value)}
                        placeholder="es. Orizzontale, al buio, senza vibrazioni"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Abbinamenti Consigliati
                    {formData.pairings && formData.pairings.length > 0 && (
                      <span className="ml-2 text-xs text-wine-purple">✨ Suggerito dall'AI</span>
                    )}
                  </label>
                  <Input
                    value={formData.pairings?.join(', ') || ''}
                    onChange={(e) => {
                      const pairingsArray = e.target.value
                        .split(',')
                        .map(p => p.trim())
                        .filter(p => p.length > 0);
                      updateFormField('pairings', pairingsArray.length > 0 ? pairingsArray : []);
                    }}
                    placeholder="es. Carni rosse, formaggi stagionati, selvaggina"
                  />
                  <p className="mt-1 text-xs text-gray-500">Separa gli abbinamenti con virgole</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note Personali</label>
                  <textarea
                    value={formData.personalNotes || ''}
                    onChange={(e) => updateFormField('personalNotes', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wine-purple focus:border-transparent"
                    placeholder="Occasione, ricordi, abbinamenti provati..."
                  />
                </div>

                {/* Recensione (solo per vini bevuti in modalità modifica) */}
                {isEditMode && existingWine?.status === 'consumed' && (
                  <div className="space-y-4 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recensione</h3>

                    {/* Rating */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Valutazione</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => updateFormField('rating', star)}
                            className={`text-3xl transition-all ${
                              formData.rating && star <= formData.rating
                                ? 'text-yellow-400 hover:text-yellow-500'
                                : 'text-gray-300 hover:text-yellow-200'
                            }`}
                          >
                            ★
                          </button>
                        ))}
                        {formData.rating && (
                          <button
                            type="button"
                            onClick={() => updateFormField('rating', undefined)}
                            className="ml-2 text-sm text-gray-500 hover:text-red-600"
                          >
                            Rimuovi
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Consumed Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Note di Degustazione</label>
                      <textarea
                        value={formData.consumedNotes || ''}
                        onChange={(e) => updateFormField('consumedNotes', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wine-purple focus:border-transparent"
                        placeholder="Come ti è sembrato questo vino? Note sulla degustazione..."
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => isEditMode ? navigate(`/wine/${id}`) : setStep('choose-method')}
                    fullWidth
                  >
                    Indietro
                  </Button>
                  <Button icon={<Save className="w-5 h-5" />} onClick={handleSaveWine} loading={saving} fullWidth>
                    Salva Vino
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal per rilevamento duplicati */}
      {duplicateWine && (
        <DuplicateModal
          isOpen={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          duplicateWine={duplicateWine}
          onIncrement={handleIncrementDuplicate}
          onInsertAnyway={handleInsertAnyway}
          onIgnore={handleIgnore}
          incrementing={incrementing}
        />
      )}

      {/* Modal per gestione locazioni */}
      <LocationManagementModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
      />
    </Layout>
  );
};
